// Bandit manchot — fonctions pures, sans DOM.
// Trois rouleaux : le personnage du joueur, son adversaire (jamais le même), puis l'action. La
// phrase tirée (« 🐭 prend le ❤️ de 🐱 ») est l'enjeu du labyrinthe : le premier arrivé sur l'action la fait
// à l'autre.
// Tirage bonus (drawSpin) : le troisième rouleau tombe sur un personnage au lieu d'une action. Pas de niveau à jouer, on
// relance ; deux personnages identiques doublent le gain du niveau à venir, trois identiques rapportent trois fois le gain
// tout de suite (docs/GAMEPLAY.md#bonus-draws).

const pick = (list, random) => list[Math.min(list.length - 1, Math.floor(random() * list.length))];

/**
 * Tire une distribution.
 * @param {string[]} characters au moins deux personnages
 * @param {{id:string, emoji:string}[]} actions
 * @param {() => number} [random] dans [0, 1)
 * @returns {{player:string, rival:string, action:{id:string, emoji:string}}}
 */
export function drawCast(characters, actions, random = Math.random) {
  const player = pick(characters, random);
  const rival = pick(characters.filter((c) => c !== player), random);
  return { player, rival, action: pick(actions, random) };
}

// Chances du tirage bonus : un tirage sur quatre ; parmi eux, un triple sur dix, une paire sur trois, sinon trois
// personnages différents.
export const BONUS_ODDS = Object.freeze({ chance: 0.25, triple: 0.1, pair: 1 / 3 });

/**
 * Tire les trois rouleaux : une distribution (cast), ou un tirage bonus de trois personnages.
 * @param {string[]} characters au moins trois personnages
 * @param {{id:string, emoji:string}[]} actions
 * @param {() => number} [random] dans [0, 1)
 * @param {{bonus?: 'none'|'pair'|'triple'|null, odds?: {chance:number, triple:number, pair:number}}} [options]
 *   bonus : tirage bonus imposé (tests en développement, voir forcedBonusFrom)
 * @returns {{reels: string[], cast: {player:string, rival:string, action:{id:string, emoji:string}}|null,
 *   bonus: null|'none'|'pair'|'triple'}}
 */
export function drawSpin(characters, actions, random = Math.random, { bonus = null, odds = BONUS_ODDS } = {}) {
  // Le haut de l'intervalle donne le bonus : un hasard à 0 tire une distribution, comme drawCast
  const kind = bonus ?? (random() >= 1 - odds.chance ? bonusKind(random(), odds) : null);
  if (!kind) {
    const cast = drawCast(characters, actions, random);
    return { reels: [cast.player, cast.rival, cast.action.emoji], cast, bonus: null };
  }
  const first = pick(characters, random);
  if (kind === 'triple') return { reels: [first, first, first], cast: null, bonus: kind };
  const second = pick(characters.filter((c) => c !== first), random);
  if (kind === 'pair') {
    const reels = [first, first, first];
    reels[Math.min(2, Math.floor(random() * 3))] = second; // l'intrus, sur l'un des trois rouleaux
    return { reels, cast: null, bonus: kind };
  }
  const third = pick(characters.filter((c) => c !== first && c !== second), random);
  return { reels: [first, second, third], cast: null, bonus: kind };
}

function bonusKind(roll, odds) {
  if (roll < odds.triple) return 'triple';
  if (roll < odds.triple + odds.pair) return 'pair';
  return 'none';
}

/**
 * Tirage bonus imposé par le paramètre `bonus` de l'adresse (`?bonus=pair`, `triple` ou `none`), pour le tester : au premier
 * tirage de chaque niveau, les relances restent au hasard. Le jeu ne le lit qu'en développement.
 * @returns {'none'|'pair'|'triple'|null} null si absent ou inconnu
 */
export function forcedBonusFrom(search) {
  const kind = new URLSearchParams(search).get('bonus');
  return ['none', 'pair', 'triple'].includes(kind) ? kind : null;
}

/**
 * Bande d'un rouleau : `length` symboles tirés au hasard, deux voisins jamais identiques (le
 * défilement doit se voir), le dernier étant `final`, celui sur lequel le rouleau s'arrête.
 * @param {string[]} symbols au moins deux symboles
 * @returns {string[]}
 */
export function reelStrip(symbols, final, length, random = Math.random) {
  const strip = [final];
  while (strip.length < length) {
    strip.unshift(pick(symbols.filter((sym) => sym !== strip[0]), random));
  }
  return strip;
}

/**
 * Action imposée au troisième rouleau par le paramètre `action` de l'adresse (ex. `?action=scare`),
 * pour tester un scénario ; le jeu ne le lit qu'en développement.
 * @param {string} search partie « ?… » de l'adresse
 * @returns {{id:string, emoji:string}|null} null si absent ou inconnu
 */
export function forcedActionFrom(search, actions) {
  const id = new URLSearchParams(search).get('action');
  return actions.find((action) => action.id === id) ?? null;
}

/**
 * Paramètres des textes d'une distribution : personnages, emoji de l'action, et sa phrase dans les
 * deux sens (slot.action.<id>.phrase) : `sentence` le joueur la fait à l'adversaire, `reverse` l'inverse.
 * @param {(key: string, params?: object) => string} t
 */
export function castParams(t, { player, rival, action }) {
  const phrase = (a, b) => t(`slot.action.${action.id}.phrase`, { a, b, goal: action.emoji });
  return { player, rival, goal: action.emoji, sentence: phrase(player, rival), reverse: phrase(rival, player) };
}

/**
 * Instants (en ms depuis le départ) des « tic » du tirage : un seul cliquet qui ralentit régulièrement, comme les
 * rouleaux, d'un « tic » toutes les `startGapMs` au départ à un toutes les `endGapMs` à la fin (écart multiplié à
 * chaque pas). Ils ne suivent pas symbole par symbole le défilement des trois rouleaux : celui-ci est très rapide au
 * début (des dizaines de symboles en quelques millisecondes), ce qui ne s'entendrait que comme un bourdonnement.
 * @param {number} durationMs durée du tirage (arrêt du dernier rouleau)
 * @param {{firstMs?: number, startGapMs?: number, endGapMs?: number}} [options] firstMs : premier « tic », après le coup de levier
 * @returns {number[]} croissants, tous dans [firstMs, durationMs[, jamais plus rapprochés que startGapMs
 */
export function spinTickTimes(durationMs, { firstMs = 160, startGapMs = 60, endGapMs = 240 } = {}) {
  const times = [];
  for (let t = firstMs; t < durationMs; t += startGapMs * (endGapMs / startGapMs) ** (t / durationMs)) times.push(t);
  return times;
}
